import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { dateToMonthAndYear } from '../../../../services/dateFormatter';

const getLinkedinUrl = (attrs) => {
  if (!attrs?.linkedinHandle) return null;
  return `https://www.linkedin.com/in/${attrs.linkedinHandle}`;
};

const getNodeLabelAndAvatar = (node) => {
  if (!node) return { name: '', avatarUrl: '', linkedinUrl: null };
  if (node.kind === 'user') {
    const attrs = node.user?.data?.attributes || {};
    return {
      name: attrs.name || '',
      avatarUrl: attrs.avatarUrl || '',
      linkedinUrl: null,
    };
  }
  const attrs = node.contact?.data?.attributes || {};
  return {
    name: attrs.fullName || '',
    avatarUrl: attrs.profilePicUrl || '',
    linkedinUrl: getLinkedinUrl(attrs),
  };
};

const getOverlapYearRange = (from, to) => {
  const fromLabel = from ? dateToMonthAndYear(from) : null;
  const toLabel = to ? dateToMonthAndYear(to) : null;
  if (fromLabel && toLabel) {
    return fromLabel === toLabel ? fromLabel : `${fromLabel} to ${toLabel}`;
  }
  return fromLabel || toLabel || '';
};

const formatSignalsSubline = (attributes) => {
  if (attributes?.type === 'work_overlap') {
    const orgName = attributes?.signals?.organization?.data?.attributes?.name;
    const bothCurrentlyAtCompany = attributes?.signals?.bothCurrentlyAtCompany;
    const from = attributes?.signals?.overlapFrom;
    const to = attributes?.signals?.overlapTo;
    const range = getOverlapYearRange(from, to);
    if (bothCurrentlyAtCompany && orgName) {
      return `Both currently working at ${orgName}.`;
    }
    if (orgName && range) {
      return { lines: [`Both worked at ${orgName} from ${range}.`] };
    }
    if (orgName) return `Both worked at ${orgName}.`;
    return 'Both worked at the same company.';
  }
  if (attributes?.type === 'direct') {
    return 'LinkedIn connection.';
  }
  return null;
};

export function useConnectionPathCard({ item }) {
  const currentUserId = useSelector((s) => s.user?.id);
  const pathNodes = useMemo(() => item?.attributes?.path || [], [item?.attributes?.path]);

  return useMemo(() => {
    const firstNode = pathNodes[0];
    const viaNode = pathNodes.length >= 2 ? pathNodes[pathNodes.length - 2] : firstNode;

    const viaNodeInfo = getNodeLabelAndAvatar(viaNode);
    const isTeamMember = viaNode?.kind === 'user';
    const badgeText = isTeamMember ? 'Team member' : 'Contact';
    const viaNodeId = String(viaNode?.id || viaNode?.user?.data?.id || '');
    const isYou = isTeamMember && viaNodeId === String(currentUserId);
    const viaLabel = isYou ? 'You' : viaNodeInfo.name;
    const subline = formatSignalsSubline(item?.attributes);

    const nodesForRender = pathNodes.map((node, index) => {
      const info = getNodeLabelAndAvatar(node);
      const key = `${node.kind}-${node.id}-${index}`;
      const isNodeYou =
        node.kind === 'user' &&
        (String(node.id) === String(currentUserId) || String(node?.user?.data?.id) === String(currentUserId));
      const labelName = isNodeYou ? 'You' : info.name || `${node.kind}-${node.id}`;
      const avatarName = info.name || `${node.kind}-${node.id}`;
      const isLast = index === pathNodes.length - 1;
      return {
        key,
        labelName,
        avatarName,
        avatarUrl: info.avatarUrl,
        isLast,
      };
    });

    return {
      viaLabel,
      isTeamMember,
      badgeText,
      viaLinkedinUrl: isTeamMember ? null : viaNodeInfo.linkedinUrl,
      subline,
      nodesForRender,
    };
  }, [currentUserId, item?.attributes, pathNodes]);
}
